/**
 * Copy and derived shape for extraction progress rows.
 *
 * Kept separate from the component so the wording — which is the whole point of
 * a truthful progress surface — can be asserted directly, and so the row stays
 * a rendering concern.
 */
import { ApiClientError, type AnalysisRevisionCancellationResponse } from '@contract-analyzer/api-client';

import type {
  ExtractionRun,
  ExtractionRunPhase,
} from '@/lib/extraction-runs/extraction-run-store';
import { isTerminalExtractionRunPhase } from '@/lib/extraction-runs/extraction-run-store';

const PHASE_LABELS: Record<ExtractionRunPhase, string> = {
  queued: 'Queued',
  running: 'Extracting',
  succeeded: 'Extraction complete',
  partial: 'Completed with exceptions',
  failed: 'Extraction failed',
  cancelled: 'Extraction cancelled',
};

function extractionRunPhaseLabel(phase: ExtractionRunPhase): string {
  return PHASE_LABELS[phase];
}

/** The run's state in a word: a cancelled run still finishing reads as cancelling. */
export function extractionRunStateLabel(run: Pick<ExtractionRun, 'phase' | 'cancelRequested'>): string {
  return run.cancelRequested && !isTerminalExtractionRunPhase(run.phase)
    ? 'Cancelling'
    : extractionRunPhaseLabel(run.phase);
}

/** Why a cancel request could not be made, in the reviewer's terms. */
export function describeCancellationFailure(cause: unknown): string {
  if (cause instanceof ApiClientError && cause.status === 403) {
    return 'You no longer have permission to cancel extractions in this project.';
  }
  if (cause instanceof ApiClientError && cause.code === 'earlier-extraction-running') {
    return 'An earlier extraction of this result is still running. Cancel it first, or wait for it to finish.';
  }
  return 'The extraction could not be cancelled. Try again.';
}

const NOTHING_STOPPED: Readonly<Record<Exclude<AnalysisRevisionCancellationResponse['outcome'], 'cancelled'>, string>> = {
  nothing_queued: 'Every document had already started, so nothing was skipped. The extraction will finish.',
  not_processing: 'The extraction had already finished, so there was nothing to cancel.',
};

/** What the reviewer is told when a cancel request found nothing left to stop; `null` once it stopped work. */
export function describeCancellationOutcome(outcome: AnalysisRevisionCancellationResponse['outcome']): string | null {
  return outcome === 'cancelled' ? null : NOTHING_STOPPED[outcome];
}

function pluralize(count: number, singular: string): string {
  return `${String(count)} ${singular}${count === 1 ? '' : 's'}`;
}

/**
 * The document ledger, stated plainly. Every number is a whole document the
 * server classified — there is no "roughly done" and no per-document fraction,
 * because a document is only ever complete once all of its extraction units
 * have settled successfully.
 */
function describeExtractionRunCounts(run: ExtractionRun): string {
  const counts = run.counts;
  const segments = [
    `${String(counts.succeeded)} of ${pluralize(counts.total, 'document')} extracted`,
  ];
  if (counts.partial > 0) {
    segments.push(`${String(counts.partial)} partially extracted`);
  }
  if (counts.failed > 0) {
    segments.push(`${String(counts.failed)} failed`);
  }
  if (counts.cancelled > 0) {
    segments.push(`${String(counts.cancelled)} cancelled`);
  }
  const remaining = counts.queued + counts.running;
  if (remaining > 0 && run.sectionCount === 0) {
    segments.push(`${String(remaining)} remaining`);
  }
  return segments.join(' · ');
}

/** Use actual run units for matrices, retaining the document outcome ledger. */
export function describeExtractionRunProgress(run: ExtractionRun): string {
  const documents = describeExtractionRunCounts(run);
  if (run.sectionCount === 0) return documents;
  const counts = run.unitCounts;
  const segments = [
    `${String(counts.succeeded)} of ${pluralize(counts.total, 'section')} extracted`,
  ];
  if (counts.failed > 0) segments.push(`${String(counts.failed)} failed`);
  if (counts.cancelled > 0) segments.push(`${String(counts.cancelled)} cancelled`);
  if (counts.running > 0) segments.push(`${String(counts.running)} running`);
  if (counts.queued > 0) segments.push(`${String(counts.queued)} queued`);
  // One section per document: the document ledger would only repeat the numbers.
  if (counts.total === run.counts.total) return segments.join(' · ');
  return `${segments.join(' · ')} — ${documents}`;
}

export function extractionRunProgressCounts(run: ExtractionRun) {
  return run.sectionCount > 0 ? run.unitCounts : run.counts;
}

/**
 * What cancelling a run will do, from the work the server last reported: work
 * not started is skipped, work in progress finishes. Matrix work is counted in
 * sections, everything else in documents.
 */
export function describeExtractionCancellation(run: ExtractionRun): string {
  const noun = run.sectionCount > 0 ? 'section' : 'document';
  const { queued, running } = run.unitCounts;
  const skipped =
    queued > 0 ? `${pluralize(queued, noun)} not started will be skipped` : 'Nothing is waiting to start';
  if (running === 0) return `${skipped}.`;
  const finishing = queued > 0 ? String(running) : pluralize(running, noun);
  return `${skipped}; ${finishing} in progress will finish.`;
}

/** Known work starts at zero; only a missing total is indeterminate. */
export function isExtractionRunIndeterminate(run: ExtractionRun): boolean {
  return extractionRunProgressCounts(run).total === 0;
}

/** Shares of the work, 0–1, that the meter draws from its left edge. */
export interface ExtractionRunMeterSegments {
  /** Work that extracted cleanly. */
  readonly extracted: number;
  /** Work that settled, cleanly or not; the band past `extracted` is unresolved. */
  readonly settled: number;
}

export function extractionRunMeterSegments(
  counts: Pick<ExtractionRun['counts'], 'total' | 'succeeded' | 'settled'>,
): ExtractionRunMeterSegments {
  if (counts.total === 0) {
    return { extracted: 0, settled: 0 };
  }
  return {
    extracted: counts.succeeded / counts.total,
    settled: counts.settled / counts.total,
  };
}

/** Spoken form of the meter — carries the same truth without colour or motion. */
export function extractionRunValueText(run: ExtractionRun): string {
  if (extractionRunProgressCounts(run).total === 0) {
    return `${extractionRunStateLabel(run)}. Work count not reported yet.`;
  }
  return `${describeExtractionRunProgress(run)}.`;
}

/** One calm sentence per state change, for assistive technology. */
export function extractionRunAnnouncement(run: ExtractionRun): string {
  const state = `${extractionRunStateLabel(run)}: ${run.gridLabel}.`;
  const failure =
    run.failureMessage === null ? '' : ` Failure reported: ${run.failureMessage}`;
  // Only a settled extraction carries its tally into the announcement; while
  // work is in flight the sentence changes on phase transitions alone, so a
  // long extraction is not narrated document by document.
  return isTerminalExtractionRunPhase(run.phase)
    ? `${state} ${describeExtractionRunProgress(run)}${failure}`
    : state;
}
